import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import ReferralBanner from '../../../components/ReferralBanner';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiPlus, FiSearch, FiTrash2, FiX, FiCheck, FiArrowLeft, FiUser, FiAlertCircle, FiEye, FiPhone, FiMail, FiBriefcase } = FiIcons;

const PartnerManagement = () => {
  const navigate = useNavigate();
  const { partners = [], updatePartner, deletePartner } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingPartner, setViewingPartner] = useState(null);

  const handleDelete = async (id) => {
    if (window.confirm('Er du sikker på at du vil slette denne partneren permanent?')) {
      await deletePartner(id);
    }
  };

  const handleApprove = async (id) => {
    const result = await updatePartner(id, { active: true });
    if (!result?.success) {
      window.alert('Partneren kunne ikke godkjennes. Prøv igjen.');
      return;
    }
    if(viewingPartner && viewingPartner.id === id) {
      setViewingPartner({...viewingPartner, active: true});
    }
  };

  const filtered = (partners || []).filter(p => 
    !p.is_deleted && (p.name?.toLowerCase().includes(searchTerm.toLowerCase()) || p.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b p-4 flex justify-between items-center sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/superadmin')} className="p-2 hover:bg-gray-100 rounded-lg">
            <SafeIcon icon={FiArrowLeft} />
          </button>
          <h1 className="text-xl font-bold">Partnerhåndtering</h1>
        </div>
        <div className="relative w-64">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-gray-400" />
          <input 
            type="text" 
            placeholder="Søk partner..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg" 
          />
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-[10px] font-black uppercase text-gray-400">
              <tr>
                <th className="px-6 py-4">Partner</th>
                <th className="px-6 py-4">Status & Info</th>
                <th className="px-6 py-4 text-right">Handling</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-bold text-gray-900">{p.name}</div>
                    <div className="text-xs text-gray-500 flex flex-col mt-1">
                      <span>{p.email}</span>
                      {p.phone && <span className="flex items-center gap-1 mt-1 text-gray-400"><SafeIcon icon={FiPhone} /> {p.phone}</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="mb-2">
                      <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase ${p.active ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {p.active ? 'Aktiv' : 'Venter'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400">
                      Søkt: {p.created_at ? new Date(p.created_at).toLocaleDateString('no-NO') : 'Ukjent'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {!p.active && (
                        <button 
                          onClick={() => handleApprove(p.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Godkjenn"
                        >
                          <SafeIcon icon={FiCheck} />
                        </button>
                      )}
                      <button 
                        onClick={() => setViewingPartner(p)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Vis detaljer"
                      >
                        <SafeIcon icon={FiEye} />
                      </button>
                      <button 
                        onClick={() => handleDelete(p.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Slett permanent"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {viewingPartner && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setViewingPartner(null)}>
            <motion.div 
              initial={{ scale: 0.9 }} 
              animate={{ scale: 1 }} 
              className="bg-white rounded-3xl w-full max-w-3xl p-8"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-black">{viewingPartner.name}</h2>
                  <div className="flex items-center gap-3 mt-2 text-gray-500">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase ${viewingPartner.active ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {viewingPartner.active ? 'Aktiv' : 'Venter på godkjenning'}
                    </span>
                    <span className="text-sm">Søkt: {viewingPartner.created_at ? new Date(viewingPartner.created_at).toLocaleDateString('no-NO') : 'Ukjent dato'}</span>
                  </div>
                </div>
                <button onClick={() => setViewingPartner(null)} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
                  <SafeIcon icon={FiX} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
                  <h3 className="text-xs font-black text-gray-400 uppercase mb-4 tracking-wider">Kontaktinfo</h3>
                  <div className="space-y-3 text-sm text-gray-700">
                    <p className="flex items-center gap-3"><SafeIcon icon={FiUser} className="text-gray-400" /> <span className="font-medium">{viewingPartner.contact_person || 'Ikke oppgitt'}</span></p>
                    <p className="flex items-center gap-3"><SafeIcon icon={FiMail} className="text-gray-400" /> {viewingPartner.email}</p>
                    <p className="flex items-center gap-3"><SafeIcon icon={FiPhone} className="text-gray-400" /> {viewingPartner.phone || 'Ikke oppgitt'}</p>
                    <p className="flex items-center gap-3"><SafeIcon icon={FiBriefcase} className="text-gray-400" /> Org.nr: {viewingPartner.org_nr || 'Ikke oppgitt'}</p>
                  </div>
                </div>
                
                <div className="bg-blue-50 p-5 rounded-2xl border border-blue-100">
                  <h3 className="text-xs font-black text-blue-400 uppercase mb-4 tracking-wider">Søknadstekst</h3>
                  <p className="text-sm text-blue-900 whitespace-pre-wrap leading-relaxed">
                    {viewingPartner.reason || 'Ingen spesifikk begrunnelse oppgitt under registrering.'}
                  </p>
                </div>
              </div>

              {viewingPartner.active ? (
                <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
                  <h3 className="text-sm font-bold text-gray-900 mb-3">Partnerens Verve-lenke</h3>
                  <ReferralBanner id={viewingPartner.partner_id || viewingPartner.id} type="partner" name={viewingPartner.name} />
                </div>
              ) : (
                <div className="bg-yellow-50 p-6 rounded-2xl flex items-start gap-4 border border-yellow-200 shadow-sm">
                  <SafeIcon icon={FiAlertCircle} className="text-yellow-600 text-2xl mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-yellow-900 text-lg">Søknad krever godkjenning</h4>
                    <p className="text-sm text-yellow-800 mt-2 leading-relaxed">
                      Denne partneren har ikke fått tilgang enda. Godkjenn partneren for å generere verve-lenke og gi dem tilgang til sin partnerportal.
                    </p>
                    <div className="mt-4 flex gap-3">
                      <button 
                        onClick={() => handleApprove(viewingPartner.id)} 
                        className="px-6 py-3 bg-green-600 text-white text-sm font-bold rounded-xl hover:bg-green-700 flex items-center gap-2 shadow-md transition-all"
                      >
                        <SafeIcon icon={FiCheck} /> Godkjenn Partner
                      </button>
                      <button 
                        onClick={() => { handleDelete(viewingPartner.id); setViewingPartner(null); }} 
                        className="px-6 py-3 bg-white text-red-600 border border-red-200 text-sm font-bold rounded-xl hover:bg-red-50 flex items-center gap-2 transition-all"
                      >
                        <SafeIcon icon={FiTrash2} /> Avslå og Slett
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PartnerManagement;
